/**
 * @module utils/panelResize
 * @description Reusable panel resize logic for draggable panel borders
 *
 * @example
 * import { createPanelResizer } from '@/utils/panelResize';
 *
 * const resizer = createPanelResizer({
 *   minWidth: 150,
 *   maxWidth: 500,
 *   direction: 'right',
 *   onResize: (width) => dispatch('resize', { width }),
 * });
 *
 * <div on:mousedown={resizer.start} />
 *
 * onDestroy(resizer.cleanup);
 */

export interface PanelResizerOptions {
  /** Minimum panel width in pixels */
  minWidth: number;
  /** Maximum panel width in pixels */
  maxWidth: number;
  /** Direction of resize handle relative to panel ('left' or 'right') */
  direction: 'left' | 'right';
  /** Callback when width changes */
  onResize: (width: number) => void;
  /** Optional callback when resize starts */
  onResizeStart?: () => void;
  /** Optional callback when resize ends */
  onResizeEnd?: () => void;
}

export interface PanelResizer {
  /** Start resize on mousedown */
  start: (event: MouseEvent) => void;
  /** Cleanup event listeners (call in onDestroy) */
  cleanup: () => void;
  /** Check if currently resizing */
  isResizing: () => boolean;
}

/**
 * Create a panel resizer with drag functionality
 */
export function createPanelResizer(options: PanelResizerOptions): PanelResizer {
  const { minWidth, maxWidth, direction, onResize, onResizeStart, onResizeEnd } = options;

  let isResizing = false;
  let startX = 0;
  let startWidth = 0;

  function handleMouseMove(event: MouseEvent): void {
    if (!isResizing) return;

    // Calculate delta based on direction
    const delta = direction === 'right' ? event.clientX - startX : startX - event.clientX;

    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
    onResize(newWidth);
  }

  function handleMouseUp(): void {
    if (!isResizing) return;

    isResizing = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    onResizeEnd?.();
  }

  function start(event: MouseEvent): void {
    // Get current width from the element or use a default
    const target = event.currentTarget as HTMLElement;
    const panel = target.parentElement;
    startWidth = panel?.offsetWidth ?? minWidth;

    isResizing = true;
    startX = event.clientX;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    onResizeStart?.();
  }

  function cleanup(): void {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }

  return {
    start,
    cleanup,
    isResizing: () => isResizing,
  };
}
